import { HttpClient } from "@angular/common/http";
import { Injectable, InjectionToken, Optional } from "@angular/core";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { AppConfigService, LoggingService } from "src/app/core";
import { EventLocalStorageService } from "src/app/features/events/shared/services/event-local-storage.service";
import { HelperMethods } from "src/app/shared";
import { DesignGameMode, ShowroomOwner, ShowroomWorkflow } from "../../models";
import { ShowroomService } from "../../services";
import { ShowroomShortlistCommands } from "../commands/showroom-shortlist-commands";
import { ShowroomShortlistItem } from "../models/showroom-shortlist-item";
import { GetShowroomShortlistResponse, ShowroomShortlistResponses } from "../responses/showroom-shortlist-responses";

export const COMPONENT_OVERLAY_REF = new InjectionToken("COMPONENT_OVERLAY_REF");

@Injectable()
export class ShowroomShortlistService {
  constructor(
    @Optional() private _eventLocalStorageService: EventLocalStorageService,
    private _http: HttpClient,
    private _appConfigService: AppConfigService,
    private _loggingService: LoggingService,
    private _showroomService: ShowroomService,
  ) {}

  isShortlistSupported(): boolean {
    const showroom = this._showroomService.currentShowroom();

    if (!showroom) {
      return false;
    }

    return (
      showroom.owner === ShowroomOwner.Vendor &&
      !showroom.underConstruction &&
      !!showroom.layouts?.find(layout => layout.mode === DesignGameMode.ExploreShowroom)
    );
  }

  getShortlistVariantSkus$(eventId: number): Observable<number[]> {
    return of(this._getEventLocalStorageService().getShortlistSkus(eventId));
  }

  setShortlist(eventId: number, shortlistItems: ShowroomShortlistItem[]): void {
    this._getEventLocalStorageService().setShortlist(eventId, {
      variantSkuIds: shortlistItems.map(item => item.variantSkuId),
    });
  }

  getShortlistItems$(variantSkus: number[]): Observable<ShowroomShortlistItem[]> {
    return this._http
      .post<GetShowroomShortlistResponse>(
        this._appConfigService.getProtectedConfig().showroomGuestShortlistApiUrl,
        ShowroomShortlistCommands.getGetShowroomShortlistCommand(variantSkus),
      )
      .pipe(map(response => ShowroomShortlistResponses.getShowroomShortlist(response)));
  }

  addItemsToShortlist(eventId: number, variantSkuIds: number[], source: string): void {
    let shortlist = this._getEventLocalStorageService().getShortlist(eventId);

    if (!shortlist?.variantSkuIds) {
      shortlist = { variantSkuIds: variantSkuIds };

      this._loggingService.logWorkflow(
        "Adding item to NEW local shortlist",
        "ShowroomShortlistService.addItemToShortlist",
        ShowroomWorkflow.Shortlist,
        "AddItem",
        source,
        {
          VariantSkuIds: `${JSON.stringify(variantSkuIds)}`,
          NumShortlistItems: `${shortlist.variantSkuIds.length}`,
        },
      );
    } else {
      shortlist.variantSkuIds.push(...variantSkuIds);

      this._loggingService.logWorkflow(
        "Adding item to EXISTING local shortlist",
        "ShowroomShortlistService.addItemToShortlist",
        ShowroomWorkflow.Shortlist,
        "AddItem",
        source,
        {
          VariantSkuIds: `${JSON.stringify(variantSkuIds)}`,
          NumShortlistItems: `${shortlist.variantSkuIds.length}`,
        },
      );
    }

    this._getEventLocalStorageService().setShortlist(eventId, shortlist);
  }

  removeItemsFromShortlist(eventId: number, variantSkuIds: number[], source: string): void {
    let shortlist = this._getEventLocalStorageService().getShortlist(eventId);

    variantSkuIds.forEach(variantSkuId => {
      HelperMethods.removeItem(shortlist.variantSkuIds, variantSkuId);
    });

    this._loggingService.logWorkflow(
      "Removing item from shortlist",
      "ShowroomShortlistService.removeItemsFromShortlist",
      ShowroomWorkflow.Shortlist,
      "RemoveItem",
      source,
      {
        VariantSkuIds: `${JSON.stringify(variantSkuIds)}`,
        NumShortlistItems: `${shortlist.variantSkuIds.length}`,
      },
    );

    this._getEventLocalStorageService().setShortlist(eventId, shortlist);
  }

  private _getEventLocalStorageService(): EventLocalStorageService {
    if (!this._eventLocalStorageService) {
      throw new Error("Showroom Shortlist Service, attempting to use eventLocalStorageService without instance.");
    }

    return this._eventLocalStorageService;
  }
}
